import { Component, EventEmitter, Input, input, Output, output, signal } from '@angular/core';
import { PAYMENT_METHOD_TYPE_DISPLAY, PAYMENT_METHOD_TYPE_TO_DISPLAY_NAMES } from '../../../models';
import { PaymentPlatformService } from '../../../services';
import { map } from 'rxjs/operators';
import { PaymentMethodDisplayNames, PaymentMethodType } from '@aiwyn/api';
import {
  CountryAlpha2Code,
  DEFAULT_LOADABLE_STATE,
  emitValueIfValid,
  getLoadedResults,
  ignoreNullish,
  Loadable,
  Logger,
  removeArrayItem,
} from '@aiwyn/shared';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { ConfigService } from '@aiwyn/config';

const PAYMENT_METHOD_LOGO_DIRECTORY = 'assets/payment-methods/';

interface NewPaymentMethodTypeOptionDisplay {
  type: PaymentMethodType;
  icon: 'generic' | 'stripe-bank';
  name: string;
}

@Component({
  selector: 'app-stripe-select-new-payment-method-type',
  templateUrl: './stripe-select-new-payment-method-type.component.html',
  styleUrls: ['./stripe-select-new-payment-method-type.component.scss'],
  standalone: false,
})
export class StripeSelectNewPaymentMethodTypeComponent {
  @Output() selectedPaymentMethodType = new EventEmitter<PaymentMethodType>();

  // When locked (email-first gate active and no valid email yet), each option renders disabled (greyed,
  // lock icon, not-allowed cursor). Clicking one shakes that individual option and emits lockedAttempt so
  // the host can focus the email field, instead of opening the add-method dialog.
  locked = input(false);
  lockedAttempt = output<void>();
  shakingType = signal<PaymentMethodType | null>(null);

  paymentMethodDisplayNames$ = new BehaviorSubject<Loadable<PaymentMethodDisplayNames>>(DEFAULT_LOADABLE_STATE);
  @Input() set paymentMethodDisplayNames(paymentMethodDisplayNames: Loadable<PaymentMethodDisplayNames>) {
    emitValueIfValid(paymentMethodDisplayNames, this.paymentMethodDisplayNames$);
  }

  PAYMENT_METHOD_LOGO_DIRECTORY = PAYMENT_METHOD_LOGO_DIRECTORY;

  newPaymentMethodTypeOptions$: Observable<NewPaymentMethodTypeOptionDisplay[]> =
    combineLatest([
      this.paymentPlatformService.getNewPaymentMethodTypeOptions().pipe(ignoreNullish()),
      getLoadedResults(this.paymentMethodDisplayNames$, true),
    ]).pipe(
      map(([newPaymentMethodTypes, paymentMethodDisplayNames]) => {
        const priorityBankPaymentMethodType = this.configService.firmCountry === CountryAlpha2Code.CANADA
          ? PaymentMethodType.ACSS
          : PaymentMethodType.US_BANK_ACCOUNT;
        const priorityBankIndex = newPaymentMethodTypes.indexOf(priorityBankPaymentMethodType);
        const priorityPaymentMethodType = priorityBankIndex !== -1 ? [newPaymentMethodTypes[priorityBankIndex]] : [];
        const updatedNewPaymentMethodTypes = removeArrayItem(newPaymentMethodTypes, priorityBankIndex);

        return priorityPaymentMethodType.concat(updatedNewPaymentMethodTypes).map(newPaymentMethodTypeOption => ({
          type: newPaymentMethodTypeOption,
          icon: newPaymentMethodTypeOption === PaymentMethodType.CARD ? 'generic' : 'stripe-bank',
          name: paymentMethodDisplayNames?.[PAYMENT_METHOD_TYPE_TO_DISPLAY_NAMES[newPaymentMethodTypeOption]] || PAYMENT_METHOD_TYPE_DISPLAY[newPaymentMethodTypeOption],
        }));
      }),
    );

  constructor(
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly configService: ConfigService,
    private readonly logger: Logger,
  ) { }

  onPaymentOptionClick(type: PaymentMethodType): void {
    if (this.locked()) {
      this.lockedAttempt.emit();
      // Retrigger the shake on the clicked option only: drop the class, then re-apply it next tick so the
      // animation restarts even on repeat clicks. (animationend) clears it.
      this.shakingType.set(null);
      setTimeout(() => this.shakingType.set(type));
      return;
    }

    this.logger.debug('PAYMENT : StripeSelectNewPaymentMethodTypeComponent : onPaymentOptionClick : Clicked on', type);
    this.selectedPaymentMethodType.emit(type);
  }
}
